import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { MapAction, MapState } from '../../gk-map/models';
import { MapStateService } from '../../gk-map/services';
import { ActiveMapState } from './gk-kendo-grid-map.model';

@Injectable()
export class GkKendoGridMapService {
  $pendingMapActions = new ReplaySubject<MapAction[]>(1);
  $mapState = new BehaviorSubject(MapState.getInitialStruct());

  // Multiple map functionality
  readonly $mapStates = new BehaviorSubject<{ [key: string]: MapState[] }>({});
  readonly $activeMapState = new BehaviorSubject<ActiveMapState>(null);

  constructor(private mapStateService: MapStateService) {
    this.initializePendingActionsSubscription();
  }

  handleMapAction(mapAction: MapAction): void {
    this.$mapState.next(
      this.mapStateService.getUpdatedMapState(
        mapAction,
        this.$mapState.getValue(),
      ),
    );
  }

  dispatchToMultipleMaps(
    mapAction: MapAction,
    index?: number,
    key?: string,
  ): void {
    this.updateState(mapAction, index, key);
  }

  initMapState(mapState: MapState, key: string): void {
    const currentStates = this.$mapStates.getValue();
    const existingStates = currentStates[key] || [];
    const updatedStates = {
      ...currentStates,
      [key]: [...existingStates, mapState],
    };
    this.setActiveMapState(key, existingStates.length - 1);
    this.$mapStates.next(updatedStates);
  }

  setActiveMapState(key: string, index: number): void {
    this.$activeMapState.next({
      key,
      index,
    });
  }

  setActiveMapStateIndex(index: number): void {
    this.$activeMapState.next({
      key: this.$activeMapState.getValue()?.key ?? '',
      index,
    });
  }

  setActiveMapStateKey(key: string): void {
    this.$activeMapState.next({
      key,
      index: this.$activeMapState.getValue().index ?? 0,
    });
  }

  removeMapState(index: number, key: string): void {
    const targetedStates = this.$mapStates.getValue()[key];
    const currentStates = this.$mapStates.getValue();

    if (this.isValidIndex(index, targetedStates.length)) {
      const updatedStates = currentStates;
      updatedStates[key].splice(index, 1);
      this.$mapStates.next(updatedStates);

      const currentActiveState = this.$activeMapState.getValue();
      if (currentActiveState.key === key && currentActiveState.index >= index) {
        const newActiveIndex = Math.max(0, currentActiveState.index - 1);
        this.$activeMapState.next({
          key,
          index: newActiveIndex,
        });
      }
    }
  }

  reindexMapStates(key: string): void {
    const currentStates = this.$mapStates.getValue();
    const statesToUpdate = currentStates[key];

    if (!statesToUpdate) {
      console.warn(`reindexMapStates: Key "${key}" not found in map states.`);
      return;
    }

    const reindexedStates = statesToUpdate.map((state, index) => {
      if (state.toolbarState && state.toolbarState.toolbarIndex !== index) {
        return {
          ...state,
          toolbarState: {
            ...state.toolbarState,
            toolbarIndex: index,
          },
        };
      }
      return state;
    });

    this.$mapStates.next({
      ...currentStates,
      [key]: reindexedStates,
    });
  }

  private initializePendingActionsSubscription(): void {
    this.$pendingMapActions.subscribe((mapActions) => {
      this.handleSingleMapActions(mapActions);
      this.handleMultipleMapActions(mapActions);
    });
  }

  private handleSingleMapActions(mapActions: MapAction[]): void {
    mapActions.forEach((mapAction) => this.handleMapAction(mapAction));
  }

  private handleMultipleMapActions(mapActions: MapAction[]): void {
    const activeMapState = this.$activeMapState.getValue();
    const currentStates = this.$mapStates.getValue();

    if (!activeMapState) {
      return;
    }

    const targetedStates = currentStates[activeMapState.key];

    if (
      !targetedStates ||
      !this.isValidIndex(activeMapState.index, targetedStates.length)
    ) {
      return;
    }

    const updatedStates = currentStates;
    mapActions.forEach((mapAction) => {
      updatedStates[activeMapState.key][activeMapState.index] =
        this.mapStateService.getUpdatedMapState(
          mapAction,
          updatedStates[activeMapState.key][activeMapState.index],
        );
    });
    this.$mapStates.next(updatedStates);
  }

  private updateState(mapAction: MapAction, index: number, key: string): void {
    const targetedStates = this.$mapStates.getValue()[key];
    const currentStates = this.$mapStates.getValue();

    if (this.isValidIndex(index, targetedStates.length)) {
      const updatedStates = currentStates;
      updatedStates[key][index] = this.mapStateService.getUpdatedMapState(
        mapAction,
        currentStates[key][index],
      );
      this.$mapStates.next(updatedStates);
    }
  }

  private isValidIndex(index: number, arrayLength: number): boolean {
    return index >= 0 && index < arrayLength;
  }
}
